import { useLocation } from "react-router"
import { appRoutes } from "../../../routes"
import { useEffect, useState } from "react"

const DOCUSIGN_DEFAULT_PARAM_KEY = "event"
const DOCUSIGN_SIGNING_COMPLETE_VALUE = "signing_complete"
// Unique query param key specific to docusign redirects
export const DOCUSIGN_REDIRECT_QUERY_PARAM_KEY = "docusignRedirect"
const DOCUSIGN_STORAGE_KEY = "docusignRedirectEnum"

// Enum for the unique query param keys of the docusign redirect URLs (AUTH_AND_RELEASES, PRACTICE_AGREEMENT, DISCLOSURE)
export enum DocusignRedirectOrigin {
  AUTH_AND_RELEASES = "authAndReleases",
  PROVIDER_AUTH_AND_RELEASES = "providerAuthAndReleases",
  PRACTICE_AGREEMENT = "practiceAgreement",
  KNOX_KEENE = "knoxKeene",
  DISCLOSURE = "disclosure",
  ACCEPT_INVITE = "acceptInvite",
}

type DocusignFlowCompletenessMap = {
  [key in DocusignRedirectOrigin]?: boolean
}
interface UseDocusignRedirectReturn {
  docusignRedirectPollingRequired: boolean
  docusignRedirectOrigin: DocusignRedirectOrigin | null
}

/**
 * Custom hook to handle DocuSign redirect validation with optional flow completion tracking
 *
 * This hook performs the following:
 * 1. Maps DocuSign redirect URLs to their corresponding enum values
 * 2. Checks if current pathname matches any of the mapped routes
 * 3. Validates if the docusignRedirect query parameter matches the expected enum value
 * 4. Manages redirect state based on flow completion status
 * 5. Handles "Finish Later" and "Decline to Sign" scenarios from DocuSign
 *
 * @param {DocusignFlowCompletenessMap} flowCompletenessMap - Optional map tracking completion status of DocuSign flows
 *
 * @example
 * ```tsx
 * const { docusignRedirectPollingRequired, redirectedFromEnum } = useDocusignRedirect({
 *   [RedirectUrlEnum.AUTH_AND_RELEASES]: true, // flow is complete
 *   [RedirectUrlEnum.PRACTICE_AGREEMENT]: false, // flow is incomplete
 * });
 * ```
 *
 * @returns {UseDocusignRedirectReturn} Object containing:
 * - docusignRedirectPollingRequired: true if redirect is valid and active after successful docusign signing event
 * - docusignRedirectOrigin: the specific docusign document that triggered the redirect
 */
const useDocusignRedirect = (
  flowCompletenessMap?: DocusignFlowCompletenessMap,
): UseDocusignRedirectReturn => {
  const { pathname } = useLocation()
  const [savedDocusignRedirectEnum, setSavedDocusignRedirectEnum] =
    useState<DocusignRedirectOrigin | null>(() => {
      const stored = localStorage.getItem(DOCUSIGN_STORAGE_KEY)
      return stored ? (stored as DocusignRedirectOrigin) : null
    })

  const [wasDocumentSigned, setWasDocumentSigned] = useState<boolean>(false)

  // Save the redirect enum to local storage so that it can be retrieved on page refresh
  // We also want to cleanup the redirect after the user has signed the document
  useEffect(() => {
    if (savedDocusignRedirectEnum) {
      if (wasDocumentSigned) {
        const shouldCleanupLocalStorage =
          flowCompletenessMap?.[savedDocusignRedirectEnum]

        // Only store in localStorage if there's a boolean cleanup being passed through for the given enum
        if (typeof shouldCleanupLocalStorage === "boolean") {
          localStorage.setItem(DOCUSIGN_STORAGE_KEY, savedDocusignRedirectEnum)
          if (shouldCleanupLocalStorage) {
            localStorage.removeItem(DOCUSIGN_STORAGE_KEY)
            setSavedDocusignRedirectEnum(null)
          }
        }
      } else {
        // For "Finish Later" and "Decline to Sign" - just clean up the localStorage
        // without triggering the flow completion logic
        localStorage.removeItem(DOCUSIGN_STORAGE_KEY)
      }
    }
  }, [savedDocusignRedirectEnum, flowCompletenessMap, wasDocumentSigned])

  // Mapping of the url pathname to the associated docusign enum
  const authReleaseRoute = appRoutes.practice.LIABILITY_ROUTE
  const docusignRedirectUrlMap: Record<DocusignRedirectOrigin, string> = {
    [DocusignRedirectOrigin.AUTH_AND_RELEASES]: authReleaseRoute,
    [DocusignRedirectOrigin.PROVIDER_AUTH_AND_RELEASES]:
      appRoutes.provider.LIABILITY_INSURANCE_ROUTE,
    [DocusignRedirectOrigin.PRACTICE_AGREEMENT]:
      appRoutes.practice.PRACTICE_AGREEMENT_ROUTE,
    [DocusignRedirectOrigin.KNOX_KEENE]:
      appRoutes.practice.PRACTICE_AGREEMENT_ROUTE,
    [DocusignRedirectOrigin.DISCLOSURE]: appRoutes.provider.DISCLOSURE_ROUTE,
    [DocusignRedirectOrigin.ACCEPT_INVITE]:
      appRoutes.practiceInvitations.ACCEPT_ROUTE,
  }

  // Get the selected enum value of the docusign redirect based off current path, default to undefined if it doesn't exist
  // Note: we need an array of enum keys because we allow multiple docusign links on the same page
  const selectedRedirectEnumValuesFromPathname: DocusignRedirectOrigin[] =
    Object.entries(docusignRedirectUrlMap)
      .filter(([_, path]) => pathname.includes(path))
      .map(([key, _]) => key as DocusignRedirectOrigin)

  // Look for the docusign redirect query params value
  const docusignRedirectQueryParamValue: DocusignRedirectOrigin | null =
    new URLSearchParams(window.location.search).get(
      DOCUSIGN_REDIRECT_QUERY_PARAM_KEY,
    ) as DocusignRedirectOrigin | null

  // Clean up query parameter asynchronously after state is confirmed to be set
  // This ensures consistent behavior across React's state updates
  useEffect(() => {
    // Clean up docusign redirect param when state is confirmed
    if (
      savedDocusignRedirectEnum &&
      selectedRedirectEnumValuesFromPathname.includes(savedDocusignRedirectEnum)
    ) {
      const url = new URL(window.location.href)
      url.searchParams.delete(DOCUSIGN_REDIRECT_QUERY_PARAM_KEY)
      window.history.replaceState({}, "", url.toString())
    }

    // Check for DocuSign event parameter and set wasDocumentSigned state
    const docusignEventParam = new URLSearchParams(window.location.search).get(
      DOCUSIGN_DEFAULT_PARAM_KEY,
    )

    // Only set wasDocumentSigned to true if the event is "signing_complete"
    if (docusignEventParam === DOCUSIGN_SIGNING_COMPLETE_VALUE) {
      setWasDocumentSigned(true)
    }

    // Clean up docusign event param when present (any DocuSign event)
    if (docusignEventParam) {
      const url = new URL(window.location.href)
      url.searchParams.delete(DOCUSIGN_DEFAULT_PARAM_KEY)
      window.history.replaceState({}, "", url.toString())
    }
  }, [savedDocusignRedirectEnum, selectedRedirectEnumValuesFromPathname])

  // If the docusign redirect url is present, check if it matches the selected redirect enum
  // and set the saved state
  const isInitialDocusignRedirect: boolean =
    !!docusignRedirectQueryParamValue &&
    selectedRedirectEnumValuesFromPathname.includes(
      docusignRedirectQueryParamValue as DocusignRedirectOrigin,
    ) &&
    wasDocumentSigned
  if (isInitialDocusignRedirect && !savedDocusignRedirectEnum) {
    setSavedDocusignRedirectEnum(docusignRedirectQueryParamValue)
  }

  // Use localstorage state in case of navigation/hard refresh
  const wasPreviouslyRedirected: boolean =
    !!savedDocusignRedirectEnum &&
    selectedRedirectEnumValuesFromPathname.includes(savedDocusignRedirectEnum)

  return {
    docusignRedirectPollingRequired:
      isInitialDocusignRedirect || wasPreviouslyRedirected,
    docusignRedirectOrigin:
      docusignRedirectQueryParamValue || savedDocusignRedirectEnum,
  }
}

export default useDocusignRedirect
